import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import moment from 'moment';
import { debounce } from 'lodash';
import Skeleton from '@mui/material/Skeleton';
import PlaceholderCurve from '../../assets/images/placeholderCurve.png';
import { ArrowRight } from 'react-feather';
import { hapticsImpactLight } from '../../utils/haptics';
import { useCurrentTheme } from '../../hooks/useCurrentTheme';
import Widget from '..';
import { useNavigate } from 'react-router-dom';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { useSelector } from 'react-redux';
import { fetchQbankPerformanceStats } from '../../services/stats';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { QBANK_FAKE_DATA } from '../../utils/fakeData';
import { QBankPerformanceDataPoint, WIDGET_KEYS } from '../../types/Widgets';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface QBankPerformanceProps {
  isDemo?: boolean;
  showDelete?: boolean;
}

const QBankPerformance: React.FC<QBankPerformanceProps> = ({
  isDemo = false,
  showDelete = false,
}) => {
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [isLoading, setIsLoading] = useState(false);
  const [qbankPerformanceData, setQbankPerformanceData] = useState<
    QBankPerformanceDataPoint[]
  >([]);

  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const navigate = useNavigate();
  const { isDarkMode } = useCurrentTheme();

  useEffect(() => {
    if (primaryExam && !isDemo) {
      getQbankPerformanceStats();
    }
    if (isDemo) {
      setQbankPerformanceData(QBANK_FAKE_DATA);
    }
  }, [primaryExam, isDemo]);

  const getQbankPerformanceStats = async () => {
    if (primaryExam) {
      setIsLoading(true);
      fetchQbankPerformanceStats().then((response) => {
        setQbankPerformanceData(response);
        setIsLoading(false);
      });
    }
  };

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // State for selected month/year
  const today = moment();
  const [selectedMonth, setSelectedMonth] = useState<number>(today.month()); // 0-indexed
  const [selectedYear, setSelectedYear] = useState<number>(today.year());

  // Effect to automatically select the most recent month with data
  useEffect(() => {
    if (qbankPerformanceData.length > 0) {
      // Get all months/years present in the data
      const allMonths = Array.from(
        new Set(
          qbankPerformanceData.map((dp) => moment(dp.date).format('YYYY-MM'))
        )
      ).sort();

      if (allMonths.length > 0) {
        // Get the most recent month with data
        const mostRecentMonth = allMonths[allMonths.length - 1];
        const mostRecentMoment = moment(mostRecentMonth, 'YYYY-MM');

        // Check if current selection has no data
        const currentMonthData = qbankPerformanceData.filter((dp) => {
          const m = moment(dp.date);
          return m.month() === selectedMonth && m.year() === selectedYear;
        });

        // If current month has no data, switch to most recent month with data
        if (currentMonthData.length === 0) {
          setSelectedMonth(mostRecentMoment.month());
          setSelectedYear(mostRecentMoment.year());
        }
      }
    }
  }, [qbankPerformanceData, selectedMonth, selectedYear]);

  // Get all months/years present in the data
  const allMonths = Array.from(
    new Set(
      (qbankPerformanceData || []).map((dp) =>
        moment(dp.date).format('YYYY-MM')
      )
    )
  ).sort();

  // Filter data for selected month/year
  const filteredData = (qbankPerformanceData || []).filter((dp) => {
    const m = moment(dp.date);
    return m.month() === selectedMonth && m.year() === selectedYear;
  });

  // Sort filtered data
  const sortedData = filteredData ? [...filteredData] : [];
  sortedData.sort(
    (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
  );

  // Create labels, scores, and pointRadius arrays for filtered data
  let labels: string[] = [];
  let scores: number[] = [];
  let pointRadius: number[] = [];

  if (sortedData.length === 1) {
    const singleLabel = moment(sortedData[0].date).format('MMMM D');
    labels = ['', singleLabel, ''];
    scores = [sortedData[0].score, sortedData[0].score, sortedData[0].score];
    pointRadius = [0, 3, 0];
  } else {
    labels = sortedData.map((dp) => moment(dp.date).format('MMMM D'));
    scores = sortedData.map((dp) => dp.score);
    pointRadius = new Array(scores.length).fill(3);
  }

  const lineData = {
    labels: labels,
    datasets: [
      {
        label: 'Score',
        data: scores,
        borderColor: isDarkMode ? '#FEFEFE' : '#5a68af',
        borderWidth: 1,
        pointRadius: pointRadius,
        backgroundColor: 'transparent',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: isDarkMode ? '#FEFEFE' : '#5a68af',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Ensures dynamic resizing
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.raw !== null) {
              label += context.raw + '%';
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          display: true,
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'Poppins',
          },
        },
        border: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: 105,
        grid: { display: false },
        ticks: {
          stepSize: 10,
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'Poppins',
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          callback: function (value: any) {
            return value <= 100 ? value + '%' : '';
          },
        },
        border: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
        },
      },
    },
    animation: {
      onComplete: () => {
        const chart = chartRef.current;
        if (chart) {
          const ctx = chart.ctx;
          const gradient = ctx.createLinearGradient(
            0,
            0,
            0,
            chart.chartArea.bottom
          );
          gradient.addColorStop(
            0,
            isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(99, 133, 254, 0.5)'
          );
          gradient.addColorStop(
            1,
            isDarkMode ? 'rgba(255, 255, 255, .02)' : 'rgba(99, 133, 254, .02)'
          );
          chart.data.datasets[0].backgroundColor = gradient;
          chart.update();
        }
      },
    },
  };

  // Month selector options (sorted)
  const monthOptions = allMonths
    .map((ym) => {
      const m = moment(ym, 'YYYY-MM');
      return {
        value: ym,
        label: m.format('MMMM YYYY'),
        month: m.month(),
        year: m.year(),
      };
    })
    .sort((a, b) => {
      const dateA = moment(a.value, 'YYYY-MM');
      const dateB = moment(b.value, 'YYYY-MM');
      return dateB.valueOf() - dateA.valueOf(); // Descending order
    });

  // Handler for dropdown change
  const handleMonthChange = (event: SelectChangeEvent) => {
    const selected = monthOptions.find(
      (opt) => opt.value === event.target.value
    );
    if (selected) {
      setSelectedMonth(selected.month);
      setSelectedYear(selected.year);
    }
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.QBANK_PERFORMANCE}
      name={'qbank-performance'}
      title="QBank Performance"
      subtitle={primaryExam?.name + ' QBank performance over time'}
      showDelete={showDelete}
    >
      <div className="qbank-performance">
        {isLoading && (
          <Skeleton
            sx={{
              bgcolor: 'rgba(255,255,255,.05)',
              borderRadius: '1rem',
            }}
            variant="rectangular"
            animation="wave"
            width={'100%'}
            height={'100%'}
          />
        )}
        {!isLoading && (
          <div
            className="chart-container"
            style={
              filteredData && filteredData.length > 0
                ? {}
                : { marginTop: '1rem' }
            }
          >
            <div style={{ height: '100%', width: '100%' }}>
              {filteredData && filteredData.length > 0 ? (
                <Line
                  key={selectedMonth + selectedYear}
                  data={lineData}
                  options={chartOptions}
                  ref={chartRef}
                />
              ) : (
                <div className="no-data">
                  QBank performance will appear here.
                  <img src={PlaceholderCurve} alt="" />
                </div>
              )}
            </div>
          </div>
        )}
        {!isLoading &&
          qbankPerformanceData &&
          qbankPerformanceData.length > 0 && (
            <div className="qbank-performance_footer">
              <div className="month-selector">
                <Select
                  value={moment({
                    year: selectedYear,
                    month: selectedMonth,
                  }).format('YYYY-MM')}
                  onChange={handleMonthChange}
                  aria-label="Select Month"
                  style={monthOptions?.length > 1 ? {} : { display: 'none' }}
                >
                  {monthOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <button
                className="button button--link"
                onClick={() => {
                  navigate('/past-blocks');
                  hapticsImpactLight();
                }}
                tabIndex={0}
                aria-label="Review Previous Blocks"
              >
                Review Blocks
                <ArrowRight />
              </button>
            </div>
          )}
      </div>
    </Widget>
  );
};

export default QBankPerformance;
